import React, { useState, useCallback, useMemo, useEffect } from 'react'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import BaseCard from '../../components/cards/base_card'
import LoadingSpinner from '../../components/loading/loading_spinner'
import EnterpriseStatCard from '../../components/cards/enterprise_stat_card'
import StatCard from '../../components/cards/stat_card'
import { useOwnAsiato } from '../../hooks/use_own_asiato'
import RadioButtonGroup from '../../components/buttons/radio_button_group'
import { useInfiniteScroll } from '../../hooks/use_infinite_scroll'
import type { RadioButtonType } from '../../components/buttons/radio_button_group'
import {
  PeriodType,
  PeriodTypeValues,
  getPeriodDays,
  type PeriodTypeValue,
} from '../../enums/PeriodType'
import dayjs from 'dayjs'
import Breadcrumb from '../../components/common/breadcrumb'

type CategoryType = 'all' | 'new' | 'repeat'

const AsiatoPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypeValue>(
    PeriodType.SEVEN_DAYS
  )

  // 期間選択から日付範囲を計算
  const { start, end } = useMemo(() => {
    const now = dayjs()
    const days = getPeriodDays(selectedPeriod)
    return {
      start: now.subtract(days, 'day').format('YYYY-MM-DD'),
      end: now.format('YYYY-MM-DD'),
    }
  }, [selectedPeriod])

  const {
    isLoading,
    isLoadingMore,
    hasMore,
    asiatoList,
    statData,
    loadMore,
    changeCategory,
    changePeriod,
  } = useOwnAsiato(start, end, 'all')

  const { LoadingElement } = useInfiniteScroll(loadMore, isLoadingMore, hasMore)

  const handlePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedPeriod(e.target.value as PeriodTypeValue)
    },
    []
  )

  const handleCategoryChange = useCallback(
    (category: CategoryType) => {
      changeCategory(category)
    },
    [changeCategory]
  )

  useEffect(() => {
    changePeriod(start, end)
  }, [start, end, changePeriod])

  const categoryButtons: RadioButtonType[] = useMemo(
    () => [
      {
        label: 'すべての訪問',
        onClick: () => handleCategoryChange('all'),
      },
      {
        label: '新規訪問',
        onClick: () => handleCategoryChange('new'),
      },
      {
        label: 'リピート訪問',
        onClick: () => handleCategoryChange('repeat'),
      },
    ],
    [handleCategoryChange]
  )

  return (
    <AuthenticatedLayout>
      <div className="bg-main-lightgray min-h-screen">
        <Breadcrumb
          items={[{ label: 'ホーム', path: '/dashboard' }, { label: '足跡' }]}
        />

        {/* メインタイトルと期間選択 */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-main-black mb-2">足跡</h1>
            <p className="text-main-darkgray">
              あなたの企業ページを訪問した企業の履歴を確認できます
            </p>
          </div>
          <select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            className="px-4 py-2 bg-main-white border border-main-gray rounded-lg text-main-black text-sm font-medium shadow-sm"
          >
            {PeriodTypeValues.map((option) => (
              <option key={option.code} value={option.code}>
                {option.value}
              </option>
            ))}
          </select>
        </div>

        {/* 統計カード */}
        {statData && (
          <StatCard
            displayCount={statData.displayCount}
            displayEnterpriseCount={statData.displayEnterpriseCount}
            matchingRate={statData.matchingRate}
            periodLabel={
              PeriodTypeValues.find((option) => option.code === selectedPeriod)
                ?.value
            }
          />
        )}

        {/* 訪問企業一覧 */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4 justify-start">
              <h2 className="text-xl font-bold text-main-black">
                訪問企業一覧
              </h2>
              {isLoading && <LoadingSpinner size={20} border={2} />}
            </div>

            {/* タブ形式のフィルター */}
            <RadioButtonGroup buttons={categoryButtons} />
          </div>

          {isLoading && asiatoList.length === 0 ? (
            <div className="flex justify-center py-20">
              <LoadingSpinner />
            </div>
          ) : asiatoList.length === 0 ? (
            <BaseCard>
              <div className="text-center py-8">
                <p className="text-main-darkgray">まだ訪問企業がありません</p>
              </div>
            </BaseCard>
          ) : (
            <div className="flex flex-col gap-4">
              {asiatoList.map((asiato, index) => (
                <EnterpriseStatCard
                  key={`${asiato.enterprise.id}-${index}`}
                  asiato={asiato}
                  initialized={!isLoading}
                />
              ))}
              {hasMore && LoadingElement}
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default AsiatoPage
